/**
 * Client-side image processing utility
 * Resizes and compresses images before upload to reduce bandwidth
 * and server-side processing requirements.
 */

/**
 * Process an image file - resize and compress for upload
 * @param {File} file - The original image file
 * @param {Object} options - Processing options
 * @param {number} options.maxWidth - Maximum width (default: 800)
 * @param {number} options.maxHeight - Maximum height (default: 800)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.85)
 * @param {string} options.outputType - Output mime type (default: 'image/jpeg')
 * @returns {Promise<File>} - Processed image file
 */
export async function processImage(file, options = {}) {
  const {
    maxWidth = 800,
    maxHeight = 800,
    quality = 0.85,
    outputType = "image/jpeg",
  } = options;

  // Create an image element to load the file
  const img = await loadImage(file);

  // Calculate new dimensions maintaining aspect ratio
  const { width, height } = calculateDimensions(
    img.width,
    img.height,
    maxWidth,
    maxHeight
  );

  // Create canvas and draw resized image
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");

  // Use better image smoothing for downscaling
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  // Draw the image
  ctx.drawImage(img, 0, 0, width, height);

  // Convert to blob
  const blob = await canvasToBlob(canvas, outputType, quality);

  // Create a new File object with the same name but potentially different extension
  const extension = outputType === "image/jpeg" ? ".jpg" : ".png";
  const baseName = file.name.replace(/\.[^/.]+$/, "");
  const newFileName = `${baseName}${extension}`;

  return new File([blob], newFileName, { type: outputType });
}

/**
 * Process an image specifically for player photos (smaller size)
 * @param {File} file - The original image file
 * @returns {Promise<File>} - Processed image file
 */
export async function processPlayerPhoto(file) {
  return processImage(file, {
    maxWidth: 400,
    maxHeight: 400,
    quality: 0.85,
    outputType: "image/jpeg",
  });
}

/**
 * Load an image file into an HTMLImageElement
 * @param {File} file - The image file to load
 * @returns {Promise<HTMLImageElement>} - Loaded image element
 */
function loadImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error("Failed to load image"));
    };
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Calculate new dimensions maintaining aspect ratio
 * @param {number} originalWidth - Original image width
 * @param {number} originalHeight - Original image height
 * @param {number} maxWidth - Maximum allowed width
 * @param {number} maxHeight - Maximum allowed height
 * @returns {{ width: number, height: number }} - New dimensions
 */
function calculateDimensions(originalWidth, originalHeight, maxWidth, maxHeight) {
  // If image is already smaller than max dimensions, return original size
  if (originalWidth <= maxWidth && originalHeight <= maxHeight) {
    return { width: originalWidth, height: originalHeight };
  }

  // Calculate the ratio to scale down
  const widthRatio = maxWidth / originalWidth;
  const heightRatio = maxHeight / originalHeight;
  const ratio = Math.min(widthRatio, heightRatio);

  return {
    width: Math.round(originalWidth * ratio),
    height: Math.round(originalHeight * ratio),
  };
}

/**
 * Convert canvas to blob
 * @param {HTMLCanvasElement} canvas - The canvas element
 * @param {string} type - Mime type
 * @param {number} quality - Quality for JPEG (0-1)
 * @returns {Promise<Blob>} - The image blob
 */
function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to convert canvas to blob"));
        }
      },
      type,
      quality
    );
  });
}

/**
 * Get image dimensions from a file
 * @param {File} file - The image file
 * @returns {Promise<{ width: number, height: number }>} - Image dimensions
 */
export async function getImageDimensions(file) {
  const img = await loadImage(file);
  return { width: img.width, height: img.height };
}

/**
 * Check if an image needs processing (is larger than max dimensions)
 * @param {File} file - The image file
 * @param {number} maxWidth - Maximum width
 * @param {number} maxHeight - Maximum height
 * @returns {Promise<boolean>} - Whether processing is needed
 */
export async function needsProcessing(file, maxWidth = 800, maxHeight = 800) {
  const { width, height } = await getImageDimensions(file);
  return width > maxWidth || height > maxHeight;
}
